import { useState, useEffect } from 'react'
import { useCropsContext } from '~/common/context/crops/crops-context'
import { useFarmChildren } from '~/crop/crop-modal/hooks/use-farm-children'
import { supabaseGetter } from '~/api/supabase-getter'

/**
 * Returns a Map<plotId, cropCount> for all plots in the farm.
 * Counts include crops directly on the plot AND crops in patches under it.
 * Succession children (parent_crop_id non-null) are excluded.
 * Re-fetches whenever refreshKey changes (any crop add/edit/remove).
 */
export const usePlotCropCounts = (farmId, plots) => {
  const [countMap, setCountMap] = useState(new Map())
  const { refreshKey } = useCropsContext()
  const { childOrgs } = useFarmChildren({ type: 'patch', farmId })

  useEffect(() => {
    async function fetchCounts() {
      if (!plots?.length) {
        setCountMap(new Map())
        return
      }

      const plotIds = plots.map(p => p.id)
      const patchOrgs = childOrgs.filter(o => o.organization_type === 'patch')
      const patchToPlot = new Map(patchOrgs.map(p => [p.id, p.parent_organization_id]))
      const allOrgIds = [...plotIds, ...patchOrgs.map(p => p.id)]

      const { data } = await supabaseGetter.crops.getByOrganizations(allOrgIds)
      const map = new Map()
      for (const crop of (data || [])) {
        if (crop.parent_crop_id) continue
        const plotId = patchToPlot.get(crop.organization_id) ?? crop.organization_id
        if (plotIds.includes(plotId)) {
          map.set(plotId, (map.get(plotId) || 0) + 1)
        }
      }
      setCountMap(map)
    }

    fetchCounts()
  }, [plots, childOrgs, refreshKey])

  return countMap
}
